"use client"

import { NewTabLink } from "./new-tab-link"
import { ResourceLink } from "./resource-link"
import { useMemo } from "react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { derive } from "@/lib/breach/intake"
import { STEPS, stepItems } from "@/lib/breach/steps"
import { progress, type Workspace } from "@/lib/breach/workspace"
import type { GoTo } from "./incident-nav"
import Link from "next/link"
import { DIRECTORIES } from "@/lib/breach/directory/refs"
import { NowPanel } from "./now-panel"
import { OpenQuestionsPanel } from "./open-questions"
import type { WorkspaceUpdater } from "./incident-tools"

type StepStatus = "not-started" | "in-progress" | "done"

const STATUS_META: Record<StepStatus, { label: string; className: string }> = {
  "not-started": { label: "Not started", className: "border-border bg-muted text-muted-foreground" },
  "in-progress": { label: "In progress", className: "border-accent-solid/50 bg-accent/10 text-accent-solid" },
  done: { label: "Done", className: "border-green-500/40 bg-green-500/10 text-green-700 dark:text-green-400" },
}

function statusOf(done: number, total: number): StepStatus {
  if (total === 0 || done === 0) return "not-started"
  return done === total ? "done" : "in-progress"
}

/** The plan index: the open questions, then the nine steps as cards, with progress and a regimes-to-ask-about box. */
export function IncidentIndex({ w, update, go }: { w: Workspace; update: WorkspaceUpdater; go: GoTo }) {
  const derived = useMemo(() => derive(w.intake), [w.intake])

  const rows = useMemo(() => STEPS.map((s) => {
    const items = stepItems(s, w.intake)
    const p = progress(w, items.map((i) => i.id))
    return { step: s, p, status: statusOf(p.done, p.total) }
  }), [w])

  const nextIdx = rows.findIndex((r) => r.status !== "done")

  return (
    <div className="grid gap-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold sm:text-3xl">Your incident plan</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Nine steps for the first hours and days. Work in order, or jump to what you need.
          </p>
        </div>
        <button type="button"
          className="rounded text-sm text-muted-foreground underline hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-solid print:hidden"
          onClick={() => go({ v: "intro", p: 0 })}>
          Re-read the intro
        </button>
      </div>

      <NowPanel w={w} update={update} go={go} />

      <OpenQuestionsPanel workspace={w} update={update} nav={go} variant="full" />

      <section className="rounded-lg border border-border bg-card p-4 print:hidden" aria-labelledby="after-h">
        <h2 id="after-h" className="font-semibold">After the incident</h2>
        <p className="mt-1 text-sm text-muted-foreground">When things are stable, use what happened to improve your security, using the NIST Cybersecurity Framework.</p>
        <button className="mt-2 text-sm underline" onClick={() => go({ v: "harden", p: 0 })}>Make it harder next time</button>
      </section>

      <section className="rounded-lg border border-border bg-card p-4 print:hidden" aria-labelledby="res-h">
        <h2 id="res-h" className="font-semibold">Resources</h2>
        <p className="mt-1 text-sm text-muted-foreground">Directories to use alongside your plan. Each step points to the right one.</p>
        <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm">
          {DIRECTORIES.map((d) => <li key={d.id}><ResourceLink href={d.href} className="underline">{d.title}</ResourceLink></li>)}
        </ul>
      </section>

      {(derived.regimes.length > 0 || derived.statesNotCovered || derived.nonUS) && (
        <section className="rounded-lg border border-border bg-card p-4" aria-labelledby="regimes">
          <h2 id="regimes" className="font-semibold">Rules to ask your lawyer about</h2>
          <p className="mt-1 text-sm text-muted-foreground">Based on your answers. These <strong>may</strong> apply. This is not a legal determination and deadlines are not shown yet. Confirm each with counsel.</p>
          <ul className="mt-3 grid gap-2 text-sm">
            {derived.regimes.map((r) => (
              <li key={r.id}><a href={r.url} target="_blank" rel="noopener" className="font-medium underline">{r.name}</a> — {r.why}</li>
            ))}
            {derived.statesNotCovered && <li><strong>State breach-notification laws</strong> — these also apply to most US incidents and are <em>not yet covered</em> by this tool. Ask counsel or your state attorney general&apos;s office.</li>}
            {derived.nonUS && <li><strong>Laws outside the US</strong> (e.g., GDPR/UK GDPR) — not covered by this tool. Ask counsel.</li>}
          </ul>
        </section>
      )}

      <ol className="grid gap-3">
        {rows.map(({ step: s, p, status }, idx) => {
          const isNext = idx === nextIdx
          const meta = STATUS_META[status]
          const action = status === "done" ? "Review" : status === "in-progress" ? "Continue" : "Start"
          return (
            <li key={s.id}
              className={cn(
                "rounded-lg border bg-card p-4",
                isNext ? "border-accent-solid/60 ring-1 ring-accent-solid/30" : "border-border",
              )}>
              <div className="flex flex-wrap items-start justify-between gap-3 sm:gap-4">
                <div className="flex min-w-0 gap-3">
                  <span aria-hidden className="text-2xl font-bold leading-none text-muted-foreground">{s.n}</span>
                  <div className="min-w-0">
                    <h2 className="text-lg font-semibold leading-snug">{s.title}</h2>
                    <p className="mt-0.5 text-sm text-muted-foreground">{s.summary}</p>
                    <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                      <span className="text-muted-foreground">{s.minutes}</span>
                      <span aria-hidden className="text-muted-foreground">·</span>
                      <span className={cn("font-medium", status === "done" && "text-green-600 dark:text-green-400")}>
                        {p.done}/{p.total} done
                      </span>
                      <span className={cn("rounded-full border px-2 py-0.5 font-medium", meta.className)}>{meta.label}</span>
                      {isNext && (
                        <span className="rounded-full border border-accent-solid/50 bg-accent/10 px-2 py-0.5 font-medium text-accent-solid">
                          Next up
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <Button
                  className="shrink-0 bg-accent-solid text-accent-solid-foreground hover:bg-accent-solid/90 print:hidden"
                  onClick={() => go({ v: "step", step: s.id, p: 0 })}>
                  {action}
                </Button>
              </div>
            </li>
          )
        })}
      </ol>
    </div>
  )
}
